import React from 'react';
import ResourceCard from './ResourceCard';

/**
 * ResourceList Component
 * Renders a list of resources with optional grouping and filtering.
 * 
 * @param {Object} props
 * @param {Array} props.items - List of resource items.
 * @param {string} props.type - Type of resources: 'worker' | 'vehicle' | 'kolonne'.
 * @param {string} [props.search=''] - Current search term.
 * @param {Array} [props.categories=[]] - Optional categories for grouping (e.g. for vehicles).
 * @param {Array} [props.assignedIds=[]] - List of IDs currently assigned.
 * @param {boolean} [props.isEditMode=false] - Whether to show action buttons.
 * @param {Object} [props.expandedGroups={}] - Object of expanded category IDs { id: boolean }.
 * @param {Function} [props.onToggleGroup] - Callback to toggle category expansion.
 * @param {Function} [props.getConflicts] - Function to get conflicts for an item ID.
 * @param {Function} [props.getStatus] - Function to get status for an item ID.
 * @param {Object} [props.cardProps] - Additional props for the ResourceCard.
 * @returns {JSX.Element}
 */
export default function ResourceList({
    items = [],
    type,
    search = '',
    categories = [],
    assignedIds = [],
    isEditMode = false,
    expandedGroups = {},
    onToggleGroup,
    getConflicts,
    getStatus,
    onDoubleClick,
    ...cardProps
}) {
    const searchTerm = search.toLowerCase();

    const filterItem = (item) => {
        const typePrefix = type === 'worker' ? 'worker_' : type === 'vehicle' ? 'vehicle_' : 'kolonne_';
        const typeSafeId = `${typePrefix}${item.id}`;
        
        if (cardProps.hideAssigned) {
            if (type === 'kolonne') {
                const memberIds = (item.members || []).map(m => `${m.type}_${m.resource_id}`);
                if (memberIds.length > 0 && memberIds.every(mid => assignedIds.includes(mid))) {
                    return false;
                }
            } else if (assignedIds.includes(typeSafeId)) {
                return false;
            }
        }

        if (!searchTerm) return true;
        
        if (type === 'vehicle') {
            return (item.license_plate || '').toLowerCase().includes(searchTerm) || 
                   (item.internal_name || '').toLowerCase().includes(searchTerm);
        }
        
        if (type === 'kolonne') {
            return (item.name || '').toLowerCase().includes(searchTerm) || 
                   (item.code || '').toLowerCase().includes(searchTerm);
        }
        
        return (item.name || '').toLowerCase().includes(searchTerm) || 
               (item.role || '').toLowerCase().includes(searchTerm);
    };

    const filteredItems = items.filter(filterItem);

    const renderItem = (item) => {
        const id = String(item.id);
        
        let isAssigned = false;
        if (type === 'kolonne') {
            // A kolonne is considered assigned if all its worker/vehicle members are in assignedIds
            const memberWorkerIds = (item.members || []).filter(m => m.type === 'worker').map(m => String(m.resource_id));
            const memberVehicleIds = (item.members || []).filter(m => m.type === 'vehicle').map(m => String(m.resource_id));
            
            isAssigned = memberWorkerIds.length > 0 || memberVehicleIds.length > 0;
            if (isAssigned) {
                isAssigned = memberWorkerIds.every(mid => assignedIds.includes(mid)) && 
                             memberVehicleIds.every(mid => assignedIds.includes(mid));
            }
        } else {
            const prefix = type === 'worker' ? 'worker_' : 'vehicle_';
            isAssigned = assignedIds.includes(`${prefix}${item.id}`);
        }

        const conflict = getConflicts ? getConflicts(item.id) : null;
        const status = getStatus ? getStatus(item.id) : null;

        return (
            <ResourceCard
                key={item.id}
                resource={item}
                type={type}
                isAssigned={isAssigned}
                isEditMode={isEditMode}
                conflict={conflict}
                status={status}
                onDoubleClick={onDoubleClick}
                {...cardProps}
            />
        );
    };

    // Special rendering for grouped vehicles
    if (type === 'vehicle' && categories.length > 0) {
        return (
            <div className="resource-list grouped">
                {categories.map(cat => {
                    const catItems = filteredItems.filter(v => String(v.category_id) === String(cat.id));
                    if (catItems.length === 0) return null;

                    const isExpanded = searchTerm || expandedGroups[cat.id] !== false;

                    return (
                        <div key={cat.id} className="vehicle-category-group">
                            <div className="vehicle-category-header" onClick={() => onToggleGroup && onToggleGroup(cat.id)}>
                                <span className={`category-chevron ${isExpanded ? 'active' : ''}`}>{isExpanded ? '▼' : '▶'}</span>
                                <div className="category-color-dot" style={{ backgroundColor: cat.color || '#cbd5e1' }} />
                                <span className="category-name">{cat.name}</span>
                                <span className="category-count">{catItems.length}</span>
                            </div>
                            {isExpanded && (
                                <div className="vehicle-items-sublist px-2 py-1">
                                    {catItems.map(renderItem)}
                                </div>
                            )}
                        </div>
                    );
                })}
                
                {/* Items without category */}
                {(() => {
                    const noCatItems = filteredItems.filter(v => !v.category_id);
                    if (noCatItems.length === 0) return null;
                    const isExpanded = searchTerm || expandedGroups['none'] !== false;

                    return (
                        <div className="vehicle-category-group">
                            <div className="vehicle-category-header" onClick={() => onToggleGroup && onToggleGroup('none')}>
                                <span className={`category-chevron ${isExpanded ? 'active' : ''}`}>{isExpanded ? '▼' : '▶'}</span>
                                <div className="category-color-dot" style={{ backgroundColor: '#cbd5e1' }} />
                                <span className="category-name">Ohne Typ</span>
                                <span className="category-count">{noCatItems.length}</span>
                            </div>
                            {isExpanded && (
                                <div className="vehicle-items-sublist px-2 py-1">
                                    {noCatItems.map(renderItem)}
                                </div>
                            )}
                        </div>
                    );
                })()}
            </div>
        );
    }

    // Default flat list
    return (
        <div className="resource-list">
            {filteredItems.map(renderItem)}
            {filteredItems.length === 0 && (
                <div className="empty-state">
                    Keine {type === 'worker' ? 'Mitarbeiter' : type === 'vehicle' ? 'Fahrzeuge' : 'Kolonnen'} gefunden
                </div>
            )}
        </div>
    );
}
